import createClient, { MaybeOptionalInit } from 'openapi-fetch';
import type { RequiredKeysOf } from 'openapi-typescript-helpers';

import { fetchData } from './fetch';
import type { paths } from '../../unifiedapi'; // generated by openapi-typescript
import { UnifiedApiMethodsResponse, UnifiedApiPaths } from '@/types/mercury-data-types/unifiedapi';

export type InitParam<Init> =
  RequiredKeysOf<Init> extends never
    ? [(Init & { [key: string]: unknown })?]
    : [Init & { [key: string]: unknown }];

export const openapiClient = createClient<paths>({
  baseUrl: '/api/v0',
  fetch: async (input) => {
    const request = input.clone();
    const headers = new Headers(request.headers);
    const source = headers.get('source');
    // Use .blob() so binary bodies (e.g. file uploads) aren't UTF-8 corrupted.
    const body = await request.blob().then((b) => (b.size ? b : undefined));

    return fetchData(
      {
        endpoint: request.url,
        useCompleteEndpoint: true,
        context: { source },
        resolveOnNoContent: request.method === 'DELETE',
        options: {
          method: request.method,
          body,
          headers: request.headers,
          priority: (input as any).priority,
        },
      },
      input.signal
    );
  },
});

export const openApiFetch = async <TPath extends UnifiedApiPaths>(
  path: TPath,
  ...init: InitParam<MaybeOptionalInit<paths[TPath], 'get'>>
) => {
  const response = await openapiClient.GET(path, ...init);

  if (response.error) throw new Error(JSON.stringify(response.error));

  return response.data as UnifiedApiMethodsResponse<TPath>;
};

// Define return type for safe promise
type SafePromiseResult<T> = {
  data: T | null;
  error?: string | null;
};

// Generic helper to wrap promises to never reject
export const safePromise = async <T>(promise: Promise<T>): Promise<SafePromiseResult<T>> => {
  try {
    const result = await promise;
    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
